import React, { useEffect, useState } from "react";
import {
    fetchAdvancedMedicalEquipmentCategoryList,
    addAdvancedMedicalEquipmentCategory,
    editAdvancedMedicalEquipmentCategory,
    updateAdvancedMedicalEquipmentCategory,
    trashAdvancedMedicalEquipmentCategory,
} from "../../Adapters/ApiAdapter";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseImage_path } from "../../variables/Variables";

export const AdvancedMedicalEquipmentCategoryList = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = add, object = edit
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetchAdvancedMedicalEquipmentCategoryList();
            setCategories(res.data.sort((a, b) => b.id - a.id));
        } catch (e) {
            toast.error("Failed to load categories");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openAdd = () => {
        setEditing({ category_name: "" });
        setDialogOpen(true);
    };

    const openEdit = async (id) => {
        try {
            const res = await editAdvancedMedicalEquipmentCategory(id);
            setEditing({ ...res.data });
            setDialogOpen(true);
        } catch {
            toast.error("Failed to fetch category");
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append("category_name", editing.category_name); // ✅ string, not object

            if (editing.id) {
                await updateAdvancedMedicalEquipmentCategory(editing.id, formData);
                toast.success("Category updated");
            } else {
                await addAdvancedMedicalEquipmentCategory(formData);
                toast.success("Category added");
            }

            setDialogOpen(false);
            load();
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleTrash = async (id) => {
        if (!window.confirm("Trash this category?")) return;
        try {
            await trashAdvancedMedicalEquipmentCategory(id);
            toast.success("Category trashed");
            load();
        } catch {
            toast.error("Failed to trash category");
        }
    };

    const columns = [
        { field: "id", headerName: "ID", width: 90 },
        { field: "category_name", headerName: "Category Name", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            width: 200,
            renderCell: (params) => (
                <Box>
                    <Button size="small" variant="contained" onClick={() => openEdit(params.row.id)}>
                        Edit
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        variant="contained"
                        sx={{ ml: 1 }}
                        onClick={() => handleTrash(params.row.id)}
                    >
                        Trash
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box m={2}>
            <ToastContainer />
            <Typography variant="h4" mb={2}>
                Hospital Technology – Categories
            </Typography>
            <Button variant="contained" onClick={openAdd} sx={{ mb: 2 }}>
                Add Category
            </Button>
            {loading ? (
                <CircularProgress />
            ) : (
                <DataGrid autoHeight rows={categories} columns={columns} pageSize={10} />
            )}

            {/* Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.id ? "Edit" : "Add"} Category</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Category Name"
                        value={editing?.category_name || ""}
                        onChange={(e) => setEditing({ ...editing, category_name: String(e.target.value) })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !editing?.category_name}>
                        {saving ? <CircularProgress size={24} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};